import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { IconChip } from './IconChip.js';

export interface AppRowProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Icone de l'app, rendue dans une IconChip 44. */
  icon: ReactNode;
  /** Nom de l'app (ex: « Contact », « Timesheet »). */
  name: string;
  /** Callback de clic sur la ligne. */
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}

/**
 * Ligne d'app editoriale (liste d'apps installees / app store).
 * Ref. brand book : IconChip 44 + nom Inter 600/22 tracking -0.02em ink-900,
 * gap-5, py-[1.0625rem] px-2.5, separateur border-soft. Au survol la ligne se
 * teinte (surface-1) et glisse vers la droite (padding-left 10 -> 18px).
 * L'app se distingue par son icone, jamais par une couleur.
 */
export const AppRow = forwardRef<HTMLButtonElement, AppRowProps>(function AppRow(
  { icon, name, className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'group flex w-full items-center gap-5 border-b border-border-soft py-[1.0625rem] pr-2.5 pl-2.5 text-left',
        'cursor-pointer transition-all duration-150 hover:bg-surface-1 hover:pl-[1.125rem]',
        className,
      )}
      {...props}
    >
      <IconChip size={44}>{icon}</IconChip>
      <span className="flex-1 font-sans text-[1.375rem] font-semibold tracking-[-0.02em] text-ink-900">
        {name}
      </span>
    </button>
  );
});
